import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import loaderImg from '../../images/loading-icon.png';

import useSpinner from '../../helpers/useSpinner';

import '../../styles/Loader.css';

/**
 * BrandLoader — indicador de rota inteira sem geometria esqueletável.
 *
 * <p>Uso reservado a <b>bootstrap de rota</b> (auth, redirect, resolução de
 * escopo) e visualizadores de documento/PDF/viewer. REDLINE-loading.md §2:
 * onde a forma é conhecida (tabela, form, painel) o indicador é <b>skeleton</b>,
 * não este. Aqui: quando a rota ainda não sabe o que vai desenhar.
 *
 * <p>Estrutura (hi-fi portal-loading-system.html §4): anel 68px `#d3d3d3` com
 * topo `#164E5C` girando 2s + robô 48px contra-girando 2s (fica de pé) + texto
 * opcional. Sem `<Modal>` — renderiza inline no container. Variante default
 * `route` se-centraliza verticalmente (min-height 60vh); variante `inline`
 * respeita o fluxo do caller.
 *
 * <p><b>Timeout 10s</b> → estado de erro com "Tentar de novo". Nunca gira pra
 * sempre. Se `onRetry` não vier, o botão faz `window.location.reload()`.
 *
 * <p>Compat: preserva props legadas `show` (default true) e `message` — os
 * ~20 callers de listagem/form vão migrar pra skeleton nas Fatias B/C do
 * ciclo, até lá continuam funcionando com esta assinatura.
 *
 * <p>🔴 <b>GATE 200/400 EMBUTIDO (2026-08-10).</b> O sistema de loading da casa
 * — grace de 200ms + duração mínima de 400ms — existe desde 01/08 em
 * `useSpinner`/`useSkeleton`, mas era o CHAMADOR que tinha que lembrar. Auditoria
 * do dia: <b>52 usos de {@code <Loader/>}, 3 arquivos com gate</b>, sendo dois
 * deles a própria infraestrutura. Resultado: loader piscando em toda tela cujo
 * fetch resolve em ~80ms — movimento que chama mais atenção que a espera que
 * ele deveria explicar.
 *
 * <p>Régua da casa (PO, 2026-08-10): <i>o primitivo carrega a regra</i>. O gate
 * mora aqui; os 52 call sites herdam sem mudar uma linha.
 *
 * <p>⚠️ {@code delayed={false}} pra quem precisa de indicador IMEDIATO — caso
 * raro e que agora precisa ser declarado, em vez de ser o default acidental.
 */
/**
 * Horizonte default — 10s. Serve pro uso canônico (bootstrap de rota), onde
 * 10s parado significa que algo quebrou.
 *
 * ⚠️ Operação sabidamente LONGA passa `timeoutMs` própria. A régua "nunca gira
 * pra sempre" continua; o que muda é o horizonte. Com o default, a extração por
 * IA (30-60s) mostrava "Não foi possível carregar. Verifique sua conexão." no
 * meio de uma chamada que estava indo bem — e a resposta chegava logo depois,
 * por cima do erro.
 */
const TIMEOUT_MS = 10000;

const Loader = ({ show = true, delayed = true, message, variant = 'route', onRetry, timeoutMs = TIMEOUT_MS }) => {
	const [timedOut, setTimedOut] = useState(false);
	// Grace 200ms + mínimo 400ms. Conta a partir de `show`, não do mount, pra
	// que o timeout de 10s siga medindo a espera REAL e não a exibição.
	const passedGate = useSpinner(show);

	// Sincroniza o start das animações do anel/robô com a timeline global do
	// browser via animation-delay negativo. Sem isso, cada instância nova de
	// <Loader/> reinicia a rotação do frame 0 — o que produz um "salto" visível
	// quando outro Loader foi desmontado logo antes na mesma transição
	// (StrictMode double-mount em dev, Suspense chain entre chunks lazy, ou
	// dois Loaders em pontos diferentes da árvore). O delay é o mesmo pros
	// dois elementos pra o robô continuar contra-girando exatamente contra o
	// anel — não pode desalinhar.
	//
	// CONGELADO no mount com useState lazy init. Recalcular a cada render
	// mudaria a inline style — o Chrome trata mudança de animation-delay
	// como restart, criando o efeito "salto pra outra posição" a cada
	// re-render do Loader (pai re-renderiza → propaga).
	const [animationDelay] = useState(() => {
		const now = typeof performance !== 'undefined' ? performance.now() : 0;
		return `-${(now / 1000).toFixed(3)}s`;
	});

	useEffect(() => {
		if (!show) {
			setTimedOut(false);
			return undefined;
		}
		const id = setTimeout(() => setTimedOut(true), timeoutMs);
		return () => clearTimeout(id);
	}, [show, timeoutMs]);

	if (!show) return null;
	// Fetch rápido nunca chega a desenhar. O `timedOut` escapa do gate: se a
	// coisa travou por 10s, o erro aparece mesmo que o spinner nunca tenha.
	if (delayed && !passedGate && !timedOut) return null;

	const wrapperClass = 'bl' + (variant === 'inline' ? ' bl-inline' : '');

	if (timedOut) {
		const handleRetry = () => {
			if (onRetry) onRetry();
			else window.location.reload();
		};
		return (
			<div className={wrapperClass + ' bl-error'} role="alert">
				<span className="blt">
					<Trans i18nKey="app.loader.timeout">Não foi possível carregar. Verifique sua conexão.</Trans>
				</span>
				<button type="button" className="bl-retry" onClick={handleRetry}>
					<Trans i18nKey="app.feedback.retry">Tentar de novo</Trans>
				</button>
			</div>
		);
	}

	return (
		<div className={wrapperClass} role="status" aria-live="polite">
			<span className="blm" style={{ animationDelay }}>
				<span className="blbot" style={{ animationDelay }}>
					<img src={loaderImg} alt="" />
				</span>
			</span>
			<span className="blt">
				{message ?? <Trans i18nKey="app.loader.label">Carregando…</Trans>}
			</span>
		</div>
	);
};

export default Loader;
